"use client";

import type { ClerkAPIResponseError } from "@clerk/shared/error";
import { useSignIn, useSignUp } from "@klarify/auth/client";
import { Button } from "@klarify/ui/components/ui/button";
import { useTrackSignup } from "@klarify/ui/hooks/gtm-track";
import { cn } from "@klarify/ui/lib/utils";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

interface EmailVerificationFormProps {
	forceRedirectUrl?: string;
	redirectUrl?: string;
	mode?: "signup" | "reset-password";
	onVerified?: () => void;
	onRedirectStart?: () => void;
}

export function EmailVerificationForm({
	forceRedirectUrl,
	redirectUrl,
	mode = "signup",
	onVerified,
	onRedirectStart,
}: EmailVerificationFormProps) {
	const { t } = useLingui();
	const { signUp, setActive: setActiveSignUp } = useSignUp();
	const { signIn, setActive: setActiveSignIn } = useSignIn();
	const setActive = mode === "signup" ? setActiveSignUp : setActiveSignIn;
	const liveEmailAddress =
		mode === "signup" ? signUp?.emailAddress : signIn?.identifier;
	// Clerk clears the sign-up after setActive. Keep the last address so the
	// card does not shrink while it redirects.
	const [emailAddress, setEmailAddress] = useState(liveEmailAddress);
	if (liveEmailAddress && liveEmailAddress !== emailAddress) {
		setEmailAddress(liveEmailAddress);
	}
	const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [countdown, setCountdown] = useState(30);
	const [isResending, setIsResending] = useState(false);
	const [lastAttemptedCode, setLastAttemptedCode] = useState<string>("");
	const [lastChangedIndex, setLastChangedIndex] = useState<number | null>(null);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	const trackSignup = useTrackSignup();

	// biome-ignore lint/correctness/useExhaustiveDependencies: trackSignup is an effect event handler that doesn't need to be in the dependency array
	useEffect(() => {
		if (mode === "signup") {
			trackSignup();
		}
	}, [mode]);

	// Focus first input on mount
	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	useEffect(() => {
		if (countdown > 0) {
			const timer = setTimeout(() => {
				setCountdown(countdown - 1);
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [countdown]);

	const handleResendCode = async () => {
		if (!signUp || countdown > 0) {
			return;
		}

		setIsResending(true);
		setError("");

		try {
			await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
			setCountdown(30);
		} catch (err: unknown) {
			const clerkError = err as ClerkAPIResponseError;
			setError(
				clerkError.longMessage ??
					clerkError?.errors?.[0]?.longMessage ??
					t`An error occurred while resending the code.`,
			);
		} finally {
			setIsResending(false);
		}
	};

	const handleDigitChange = (index: number, value: string) => {
		// Only allow single digit
		let newValue = value;
		if (value.length > 1) {
			newValue = value[value.length - 1] || "";
		}

		// Only allow numbers
		if (newValue && !/^\d$/.test(newValue)) {
			return;
		}

		const newDigits = [...digits];
		newDigits[index] = newValue;
		setDigits(newDigits);
		setLastChangedIndex(index);

		// Auto-focus next input and select its content
		if (newValue && index < 5) {
			const nextInput = inputRefs.current[index + 1];
			// Use setTimeout to ensure select happens after state update
			setTimeout(() => {
				nextInput?.focus();
				nextInput?.select();
			}, 0);
		}
	};

	const handleFocus = (index: number) => {
		// Select the input content when focused so typing replaces it
		inputRefs.current[index]?.select();
	};

	const handleKeyDown = (
		index: number,
		e: React.KeyboardEvent<HTMLInputElement>,
	) => {
		if (e.key === "Backspace" && !digits[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);

		if (!/^\d+$/.test(pastedData)) {
			return;
		}

		const newDigits = [...digits];
		for (let i = 0; i < pastedData.length && i < 6; i++) {
			newDigits[i] = pastedData[i] || "";
		}
		setDigits(newDigits);

		// Focus last filled input or the next empty one
		const nextIndex = Math.min(pastedData.length, 5);
		inputRefs.current[nextIndex]?.focus();
	};

	const verifyCode = useCallback(
		async (code: string) => {
			if (isSubmitting) {
				return;
			}

			// Check that we have the right object based on mode
			if (mode === "signup" && !signUp) {
				return;
			}
			if (mode === "reset-password" && !signIn) {
				return;
			}

			setIsSubmitting(true);
			setError("");
			setLastAttemptedCode(code);

			try {
				if (mode === "signup" && signUp) {
					const completeSignUp = await signUp.attemptEmailAddressVerification({
						code,
					});

					if (completeSignUp.status === "complete") {
						setIsVerified(true);
						onRedirectStart?.();
						if (setActive) {
							await setActive({ session: completeSignUp.createdSessionId });
						}
						window.location.href = forceRedirectUrl ?? redirectUrl ?? "/";
					} else {
						setError(t`Verification incomplete. Please try again.`);
						setIsSubmitting(false);
					}
				} else if (mode === "reset-password" && signIn) {
					const result = await signIn.attemptFirstFactor({
						strategy: "reset_password_email_code",
						code,
					});

					if (result.status === "needs_new_password") {
						setIsVerified(true);
						// Call onVerified callback to move to password step
						setTimeout(() => {
							onVerified?.();
						}, 500);
					} else {
						setError(t`Code verification incomplete. Please try again.`);
						setIsSubmitting(false);
					}
				}
			} catch (err: unknown) {
				const clerkError = err as ClerkAPIResponseError;
				const errorMessage =
					clerkError.longMessage ??
					clerkError?.errors?.[0]?.longMessage ??
					t`Invalid verification code. Please try again.`;
				setError(errorMessage);
				setIsSubmitting(false);
			}
		},
		[
			signUp,
			signIn,
			isSubmitting,
			setActive,
			forceRedirectUrl,
			redirectUrl,
			mode,
			onVerified,
			onRedirectStart,
			t,
		],
	);

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();
		const code = digits.join("");
		if (code.length !== 6) {
			setError(t`Please enter all ${{ count: 6 }} digits`);
			return;
		}
		await verifyCode(code);
	};

	// Auto-submit when all digits are entered and the last digit was just filled
	useEffect(() => {
		const code = digits.join("");
		// Only verify if we have 6 digits, the last digit (index 5) was just changed,
		// not currently submitting, and haven't already tried this code
		if (
			code.length === 6 &&
			lastChangedIndex === 5 &&
			!isSubmitting &&
			code !== lastAttemptedCode
		) {
			void verifyCode(code);
		}
	}, [digits, lastChangedIndex, isSubmitting, lastAttemptedCode, verifyCode]);

	return (
		<>
			<p className="mb-4 text-klarify-gray-mod-600 text-sm">
				<Trans>
					Please enter the verification code sent to{" "}
					<span className="wrap-anywhere font-medium text-klarify-gray-mod-900">
						{emailAddress ?? ""}
					</span>
				</Trans>
			</p>
			<form
				onSubmit={handleVerify}
				className="space-y-4"
			>
				<div>
					<div className="flex justify-center gap-2">
						{digits.map((digit, index) => (
							<div
								key={index}
								className="relative"
							>
								<input
									ref={(el) => {
										inputRefs.current[index] = el;
									}}
									type="text"
									inputMode="numeric"
									value={digit}
									onChange={(e) => handleDigitChange(index, e.target.value)}
									onKeyDown={(e) => handleKeyDown(index, e)}
									onFocus={() => handleFocus(index)}
									onPaste={handlePaste}
									disabled={isSubmitting}
									className={cn(
										"h-12 w-12 rounded-md border text-center font-semibold text-lg shadow-sm focus:outline-none focus:ring-1 disabled:bg-klarify-gray-mod-50",
										error
											? "border-destructive focus:border-destructive focus:ring-destructive"
											: "border-klarify-gray-mod-300 focus:border-primary focus:ring-primary",
									)}
									maxLength={1}
									required
									aria-label={t`Digit ${{ number: index + 1 }}`}
									aria-invalid={error ? "true" : "false"}
								/>
								{isVerified && digit && (
									<motion.div
										initial={{ opacity: 0, scale: 0.5 }}
										animate={{
											opacity: 1,
											scale: 1,
											transition: {
												type: "spring",
												stiffness: 300,
												damping: 20,
												delay: index * 0.05,
											},
										}}
										className="absolute inset-0 flex items-center justify-center rounded-md bg-klarify-cloud-50"
									>
										<Check className="h-6 w-6 text-klarify-forest-600" />
									</motion.div>
								)}
							</div>
						))}
					</div>
				</div>

				{error && (
					<p className="text-center text-destructive text-sm">{error}</p>
				)}

				<Button
					type="submit"
					disabled={isSubmitting}
					rounding="md"
					className="w-full"
				>
					<AnimatePresence mode="wait">
						{isVerified ? (
							<motion.span
								key="verified"
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								className="flex items-center gap-2"
							>
								{mode === "signup" ? (
									<Trans>Verified! Redirecting...</Trans>
								) : (
									<Trans>Verified!</Trans>
								)}
							</motion.span>
						) : isSubmitting ? (
							<motion.span
								key="verifying"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								className="flex items-center gap-2"
							>
								<Loader2 className="h-4 w-4 animate-spin" />
								<Trans>Verifying...</Trans>
							</motion.span>
						) : (
							<motion.span
								key="verify"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
							>
								{mode === "signup" ? (
									<Trans>Verify Email</Trans>
								) : (
									<Trans>Verify Code</Trans>
								)}
							</motion.span>
						)}
					</AnimatePresence>
				</Button>

				{mode === "signup" && (
					// Hide instead of unmount so the card keeps its height while it verifies.
					<div
						className={cn(
							"text-center text-klarify-gray-mod-600 text-sm",
							isSubmitting && "invisible",
						)}
					>
						{countdown > 0 ? (
							<p>
								<Trans>
									Didn't receive a code? Resend in{" "}
									<span className="font-medium text-primary">
										<Plural
											value={countdown}
											one="# second"
											other="# seconds"
										/>
									</span>
								</Trans>
							</p>
						) : (
							<Button
								type="button"
								onClick={handleResendCode}
								disabled={isResending}
								variant="link"
								rounding="md"
								className="font-medium text-primary hover:text-primary/90"
							>
								{isResending ? (
									<Trans>Sending...</Trans>
								) : (
									<Trans>Resend verification code</Trans>
								)}
							</Button>
						)}
					</div>
				)}
			</form>
		</>
	);
}
